import { MATTERPORT_SID_REGEX } from "./matterport";

export type MatterportViewMode = "mode.inside" | "mode.dollhouse" | "mode.floorplan";
export type MatterportViewSnapshot = {
  version: 1;
  modelId: string;
  viewId?: string;
  mode: MatterportViewMode;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number };
  sweep?: string;
  /** Camera.zoom is documented only for panorama mode. */
  zoom?: number;
};

type Subscription = { cancel(): void };
type Observable = { subscribe(callback: (value: unknown) => void): Subscription };

/** Narrow runtime boundary: hosted SDK versions may differ from the package. */
export type MatterportSessionSdk = {
  Camera?: {
    pose?: Observable;
    zoom?: Observable;
    zoomTo?: (level: number) => Promise<unknown>;
  };
  View?: { current?: Observable };
  Sweep?: {
    data?: { subscribe(observer: {
      onCollectionUpdated(collection: Record<string, unknown>): void;
    }): Subscription };
    moveTo?: (sweep: string, options: {
      rotation: { x: number; y: number };
      transition: string;
      transitionTime?: number;
    }) => Promise<unknown>;
  };
  Mode?: { moveTo?: (mode: MatterportViewMode, options: {
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number };
    transition: string;
  }) => Promise<unknown> };
};

const ID = /^[A-Za-z0-9._:-]{1,128}$/;
const MODES: MatterportViewMode[] = ["mode.inside", "mode.dollhouse", "mode.floorplan"];
export const MATTERPORT_VIEW_OPERATION_TIMEOUT_MS = 12_000;

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

function finite(value: unknown, maximum: number): value is number {
  return typeof value === "number" && Number.isFinite(value) && Math.abs(value) <= maximum;
}

export function matterportViewId(value: unknown): string | undefined {
  const id = record(value)?.id;
  return typeof id === "string" && ID.test(id) ? id : undefined;
}

export function matterportEnabledSweeps(collection: Record<string, unknown>): ReadonlySet<string> {
  return new Set(Object.values(collection).flatMap((value) => {
    const row = record(value);
    return row?.enabled === true && typeof row.id === "string" && ID.test(row.id) ? [row.id] : [];
  }));
}

/** Copy only camera values needed for navigation; never serialize SDK objects or links. */
export function snapshotMatterportView(
  modelId: string,
  value: unknown,
  viewId?: string,
  zoomValue?: unknown,
): MatterportViewSnapshot | null {
  if (!MATTERPORT_SID_REGEX.test(modelId) || (viewId !== undefined && !ID.test(viewId))) return null;
  const pose = record(value);
  const position = record(pose?.position);
  const rotation = record(pose?.rotation);
  if (!pose || !MODES.includes(pose.mode as MatterportViewMode) ||
      !position || ![position.x, position.y, position.z].every((n) => finite(n, 1_000_000)) ||
      !rotation || !finite(rotation.x, 90) || !finite(rotation.y, 360)) return null;
  const sweep = typeof pose.sweep === "string" && ID.test(pose.sweep) ? pose.sweep : undefined;
  if (pose.mode === "mode.inside" && !sweep) return null;
  const zoom = record(zoomValue)?.level;
  return {
    version: 1, modelId, ...(viewId ? { viewId } : {}), mode: pose.mode as MatterportViewMode,
    position: { x: position.x as number, y: position.y as number, z: position.z as number },
    rotation: { x: rotation.x, y: rotation.y },
    ...(sweep ? { sweep } : {}),
    ...(pose.mode === "mode.inside" && finite(zoom, 100) && zoom > 0 ? { zoom } : {}),
  };
}

/** Versioned compact tuple in a fragment: no API key, URL, tag content, or access token. */
export function encodeMatterportViewFragment(snapshot: MatterportViewSnapshot): string | null {
  const safe = snapshotMatterportView(snapshot.modelId, snapshot, snapshot.viewId, { level: snapshot.zoom });
  if (!safe || snapshot.version !== 1) return null;
  const round = (n: number) => Math.round(n * 10_000) / 10_000;
  const value = [1, safe.modelId, safe.viewId ?? null, MODES.indexOf(safe.mode),
    [safe.position.x, safe.position.y, safe.position.z].map(round),
    [safe.rotation.x, safe.rotation.y].map(round), safe.sweep ?? null, safe.zoom ?? null];
  return `#vp=${btoa(JSON.stringify(value)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")}`;
}

export function parseMatterportViewFragment(hash: string): MatterportViewSnapshot | null {
  if (!hash || hash.length > 4_096) return null;
  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const values = params.getAll("vp");
  const encoded = values[0];
  if (values.length !== 1 || !encoded || encoded.length > 2_048 || !/^[A-Za-z0-9_-]+$/.test(encoded)) return null;
  try {
    const value: unknown = JSON.parse(atob(encoded.replace(/-/g, "+").replace(/_/g, "/")));
    if (!Array.isArray(value) || value.length !== 8 || value[0] !== 1 ||
        typeof value[1] !== "string" || (value[2] !== null && typeof value[2] !== "string") ||
        !Number.isInteger(value[3]) || !MODES[value[3]] ||
        !Array.isArray(value[4]) || value[4].length !== 3 ||
        !Array.isArray(value[5]) || value[5].length !== 2 ||
        (value[6] !== null && (typeof value[6] !== "string" || !ID.test(value[6]))) ||
        (value[7] !== null && (!finite(value[7], 100) || value[7] <= 0))) return null;
    return snapshotMatterportView(value[1], {
      mode: MODES[value[3]], position: { x: value[4][0], y: value[4][1], z: value[4][2] },
      rotation: { x: value[5][0], y: value[5][1] }, sweep: value[6],
    }, value[2] ?? undefined, { level: value[7] });
  } catch { return null; }
}

/**
 * Sweep bookmark: `#sweep=<model>~<sweep>~<pitch>~<yaw>`. Names a scan point and a
 * facing only, so it opens in whichever View is active; availability still
 * requires that sweep to be enabled there. Old Treedis room links (`?s=&x=&y=`)
 * arrive in this form via `/tour/<slug>`.
 */
export function encodeMatterportSweepFragment(bookmark: {
  modelId: string; sweep: string; rotation: { x: number; y: number };
}): string | null {
  const safe = snapshotMatterportView(bookmark.modelId, {
    mode: "mode.inside", position: { x: 0, y: 0, z: 0 }, rotation: bookmark.rotation, sweep: bookmark.sweep,
  });
  if (!safe?.sweep) return null;
  const round = (n: number) => Math.round(n * 10_000) / 10_000;
  return `#sweep=${[safe.modelId, safe.sweep, round(safe.rotation.x), round(safe.rotation.y)].join("~")}`;
}

export function parseMatterportSweepFragment(hash: string, viewId?: string): MatterportViewSnapshot | null {
  if (!hash || hash.length > 1_024) return null;
  const values = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash).getAll("sweep");
  if (values.length !== 1) return null;
  const parts = values[0]!.split("~");
  if (parts.length !== 4 || !parts[2] || !parts[3]) return null;
  return snapshotMatterportView(parts[0]!, {
    mode: "mode.inside", position: { x: 0, y: 0, z: 0 },
    rotation: { x: Number(parts[2]), y: Number(parts[3]) }, sweep: parts[1],
  }, viewId);
}

export function matterportSessionKey(modelId: string, viewId?: string): string {
  return JSON.stringify([modelId, viewId ?? null]);
}

export type MatterportViewScope = {
  modelId: string;
  viewId?: string;
  enabledSweeps: ReadonlySet<string>;
};

export function isMatterportViewAvailable(snapshot: MatterportViewSnapshot, scope: MatterportViewScope): boolean {
  return snapshot.modelId === scope.modelId && snapshot.viewId === scope.viewId &&
    (snapshot.mode !== "mode.inside" || (!!snapshot.sweep && scope.enabledSweeps.has(snapshot.sweep))) &&
    (!snapshot.sweep || scope.enabledSweeps.has(snapshot.sweep));
}

/** Deadline and cancellation contain provider errors without leaving rejected promises behind. */
function bounded<T>(operation: () => Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(new Error("Viewer navigation cancelled.")); return; }
    let settled = false;
    const finish = (outcome: { value: T } | { error: unknown }) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener("abort", abort);
      if ("error" in outcome) reject(outcome.error); else resolve(outcome.value);
    };
    const abort = () => finish({ error: new Error("Viewer navigation cancelled.") });
    const timer = setTimeout(() => finish({ error: new Error("Viewer navigation timed out.") }), MATTERPORT_VIEW_OPERATION_TIMEOUT_MS);
    signal.addEventListener("abort", abort, { once: true });
    Promise.resolve().then(() => {
      if (signal.aborted) throw new Error("Viewer navigation cancelled.");
      return operation();
    }).then((value) => finish({ value }), (error: unknown) => finish({ error }));
  });
}

/**
 * Restore only inside the currently authorized model/view. Never activate a View,
 * enable a disabled sweep, or select a provider model from the fragment.
 * SDK contracts: Camera.pose/zoom, Sweep.moveTo, Mode.moveTo in @matterport/sdk.
 */
export async function restoreMatterportView({
  sdk, snapshot, getScope, signal, reducedMotion,
}: {
  sdk: MatterportSessionSdk;
  snapshot: MatterportViewSnapshot;
  getScope(): MatterportViewScope;
  signal: AbortSignal;
  reducedMotion: boolean;
}): Promise<boolean> {
  if (snapshot.version !== 1 ||
      !snapshotMatterportView(snapshot.modelId, snapshot, snapshot.viewId, { level: snapshot.zoom }) ||
      (snapshot.zoom !== undefined && (!finite(snapshot.zoom, 100) || snapshot.zoom <= 0))) return false;
  const valid = () => !signal.aborted && isMatterportViewAvailable(snapshot, getScope());
  if (!valid()) return false;
  const transition = reducedMotion ? "transition.instant" : "transition.fly";
  if (snapshot.mode === "mode.inside") {
    if (!sdk.Sweep?.moveTo || !snapshot.sweep) return false;
    await bounded(() => {
      if (!valid()) throw new Error("Viewer position is unavailable.");
      return sdk.Sweep!.moveTo!(snapshot.sweep!, {
        rotation: snapshot.rotation, transition, ...(reducedMotion ? {} : { transitionTime: 450 }),
      });
    }, signal);
    if (!valid()) return false;
    if (snapshot.zoom !== undefined && sdk.Camera?.zoomTo) {
      await bounded(() => {
        if (!valid()) throw new Error("Viewer position is unavailable.");
        return sdk.Camera!.zoomTo!(snapshot.zoom!);
      }, signal);
    }
  } else {
    if (!sdk.Mode?.moveTo) return false;
    await bounded(() => {
      if (!valid()) throw new Error("Viewer position is unavailable.");
      return sdk.Mode!.moveTo!(snapshot.mode, {
        position: snapshot.position, rotation: snapshot.rotation, transition,
      });
    }, signal);
  }
  return valid();
}
